import { ERROR_SPECS, type ApiErrorBody } from '@natashu/shared';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type InfiniteData } from '@tanstack/react-query';
import { api, ApiError, NETWORK_ERROR_MESSAGE, type QueryParams } from '../../lib/api.ts';
import type {
  ConversationDetail,
  ConversationListPage,
  FolderItem,
  InboxFilters,
  MessagesPage,
  MutationRowResult,
  ReplyResult,
  SendIntentsPage,
  UploadedMedia,
  WorkspaceUser,
} from './types.ts';

/**
 * Query keys start with the resource names that `queryKeysForEvent` (lib/realtime.ts) invalidates:
 * `conversations`, `conversation`, `messages`. Other inbox keys are refreshed explicitly.
 */
export const inboxKeys = {
  list: (filters: InboxFilters) => ['conversations', 'list', filters] as const,
  detail: (id: string) => ['conversation', id] as const,
  messages: (id: string) => ['messages', id] as const,
  intents: (id: string) => ['send-intents', id] as const,
  users: ['inbox', 'users'] as const,
  folders: ['inbox', 'folders'] as const,
  channels: ['inbox', 'channels'] as const,
};

export const LIST_PAGE_SIZE = 30;
export const MESSAGES_PAGE_SIZE = 40;

export function toListQuery(filters: InboxFilters): QueryParams {
  return {
    view: filters.view,
    mode: filters.mode,
    q: filters.q.trim() || undefined,
    channelAccountIds: filters.channelAccountIds.length ? filters.channelAccountIds.join(',') : undefined,
    providers: filters.providers.length ? filters.providers.join(',') : undefined,
    assignee: filters.assignee || undefined,
    tags: filters.tags.length ? filters.tags.join(',') : undefined,
    folderId: filters.folderId || undefined,
    folderIds: filters.folderIds.length ? filters.folderIds : undefined,
    noFolder: filters.noFolder || undefined,
    noOpenFolder: filters.noOpenFolder ? 'true' : undefined,
    related: filters.related || undefined,
    registered: filters.registered || undefined,
    registrationStatus: filters.registrationStatus || undefined,
    squad: filters.squad || undefined,
    lineConfirm: filters.lineConfirm || undefined,
    includeChildren: (filters.folderId || filters.folderIds.length) && filters.includeChildren ? 'true' : undefined,
    directOnly: (filters.folderId || filters.folderIds.length) && filters.directOnly ? 'true' : undefined,
    snoozed: filters.snoozed || undefined,
    starred: filters.starred ? 'true' : undefined,
  };
}

export function useConversationList(filters: InboxFilters, enabled = true) {
  return useInfiniteQuery({
    queryKey: inboxKeys.list(filters),
    queryFn: ({ pageParam, signal }) =>
      api.get<ConversationListPage>('/api/conversations', {
        query: { ...toListQuery(filters), limit: LIST_PAGE_SIZE, cursor: pageParam ?? undefined },
        signal,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled,
    staleTime: 10_000,
  });
}

export function useConversationDetail(id: string | undefined) {
  return useQuery({
    queryKey: inboxKeys.detail(id ?? ''),
    queryFn: ({ signal }) => api.get<ConversationDetail>(`/api/conversations/${id}`, { signal }),
    enabled: Boolean(id),
    staleTime: 10_000,
    retry: (count, err) => err instanceof ApiError && err.retryable && count < 2,
  });
}

export function useMessages(id: string | undefined) {
  return useInfiniteQuery({
    queryKey: inboxKeys.messages(id ?? ''),
    queryFn: ({ pageParam, signal }) =>
      api.get<MessagesPage>(`/api/conversations/${id}/messages`, {
        query: { limit: MESSAGES_PAGE_SIZE, before: pageParam ?? undefined },
        signal,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled: Boolean(id),
    staleTime: 10_000,
  });
}

export function useSendIntents(id: string | undefined, enabled: boolean) {
  return useInfiniteQuery({
    queryKey: inboxKeys.intents(id ?? ''),
    queryFn: ({ pageParam, signal }) =>
      api.get<SendIntentsPage>(`/api/conversations/${id}/send-intents`, { query: { limit: 20, cursor: pageParam ?? undefined }, signal }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled: Boolean(id) && enabled,
    staleTime: 5_000,
  });
}

/** Staff that chats can be assigned to (needs conversations.assign or users.manage). */
export function useWorkspaceUsers(enabled: boolean) {
  return useQuery({
    queryKey: inboxKeys.users,
    queryFn: ({ signal }) => api.get<{ items: WorkspaceUser[] }>('/api/users/assignable', { signal }),
    enabled,
    staleTime: 5 * 60_000,
    select: (d) => d.items.filter((u) => u.isActive),
  });
}

/** Conversation folders; a 404 (module not deployed) resolves to null so the picker hides. */
/** Categories of CUSTOMERS — what the shop builds by hand ("U12 › ยืนยันทีมแล้ว") and filters the inbox by. */
export function useContactFolders(enabled: boolean) {
  return useQuery({
    queryKey: [...inboxKeys.folders, 'contact'],
    queryFn: async ({ signal }) => {
      try {
        const res = await api.get<{ items: FolderItem[] }>('/api/folders', { query: { targetType: 'contact' }, signal });
        return res.items.filter((f) => !f.archivedAt && f.kind === 'manual');
      } catch (err) {
        if (err instanceof ApiError && (err.code === 'NOT_FOUND' || err.code === 'FORBIDDEN')) return [];
        throw err;
      }
    },
    enabled,
    staleTime: 60_000,
  });
}

export function useConversationFolders(enabled: boolean) {
  return useQuery({
    queryKey: inboxKeys.folders,
    queryFn: async ({ signal }) => {
      try {
        const res = await api.get<{ items: FolderItem[] }>('/api/folders', { query: { targetType: 'conversation' }, signal });
        return res.items.filter((f) => !f.archivedAt && f.targetType === 'conversation');
      } catch (err) {
        if (err instanceof ApiError && (err.code === 'NOT_FOUND' || err.code === 'FORBIDDEN')) return null;
        throw err;
      }
    },
    enabled,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export interface ChannelAccountOption {
  id: string;
  provider: string;
  name: string;
  isDemo: boolean;
  status?: string;
}

/** Customer chat accounts of the workspace (conversations.read) — several per provider are normal. */
export function useChannelAccounts(enabled: boolean) {
  return useQuery({
    queryKey: inboxKeys.channels,
    queryFn: async ({ signal }) => {
      const res = await api.get<{ items: ChannelAccountOption[] }>('/api/conversations/channel-accounts', { signal });
      return res.items;
    },
    enabled,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

// ---------------------------------------------------------------- writes

export type ReplyPart = { kind: 'text'; text: string } | { kind: 'image'; mediaId: string };

export function sendReply(conversationId: string, parts: ReplyPart[], idempotencyKey: string, scheduledAt?: string, replyToMessageId?: string) {
  return api.post<ReplyResult>(
    `/api/conversations/${conversationId}/messages`,
    { parts, ...(scheduledAt ? { scheduledAt } : {}), ...(replyToMessageId ? { replyToMessageId } : {}) },
    { idempotencyKey },
  );
}

export function addNote(conversationId: string, text: string, idempotencyKey: string) {
  return api.post<{ messageId: string; createdAt: string }>(`/api/conversations/${conversationId}/notes`, { text }, { idempotencyKey });
}

export type ConversationAction =
  | 'assign'
  | 'collaborators'
  | 'takeover'
  | 'release'
  | 'close'
  | 'reopen'
  | 'snooze'
  | 'unsnooze'
  | 'mute'
  | 'unmute'
  | 'pin'
  | 'unpin'
  | 'star'
  | 'unstar'
  | 'await-confirm'
  | 'clear-confirm'
  | 'tags'
  | 'contexts'
  | 'ai-mode';

export function conversationAction<T = MutationRowResult>(conversationId: string, action: ConversationAction, body: Record<string, unknown> = {}) {
  return api.post<T>(`/api/conversations/${conversationId}/${action}`, body);
}

/** Runs an action and refreshes the list, the detail and (for context changes) nothing else. */
export function useConversationAction(conversationId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ action, body }: { action: ConversationAction; body?: Record<string, unknown> }) =>
      conversationAction(conversationId as string, action, body ?? {}),
    onSettled: () => {
      if (!conversationId) return;
      void queryClient.invalidateQueries({ queryKey: inboxKeys.detail(conversationId) });
      void queryClient.invalidateQueries({ queryKey: ['conversations'] });
      void queryClient.invalidateQueries({ queryKey: ['inbox-counts'] });
    },
  });
}

export function markAgentRead(conversationId: string, lastReadMessageId: string) {
  return api.post<{ moved: boolean; lastReadMessageId: string | null; agentUnreadCount: number }>(`/api/conversations/${conversationId}/agent-read`, {
    lastReadMessageId,
  });
}

export interface RecordPaymentBody {
  registrationId: string;
  entryType: 'payment';
  method: 'transfer' | 'promptpay' | 'cash' | 'card' | 'other';
  amountMinor: number;
  receivedAt?: string;
  note?: string;
  slipMediaId: string;
  verifyNow: boolean;
}

export function recordSlipPayment(body: RecordPaymentBody, idempotencyKey: string) {
  return api.post<{ entry: { id: string; verificationStatus: string }; registration: unknown }>('/api/payments', body, { idempotencyKey });
}

/**
 * Multipart upload with progress (fetch has no upload progress). Uses the same headers as lib/api.ts:
 * X-Requested-With + X-CSRF-Token from the session.
 */
export function uploadImage(file: File, csrfToken: string | null, onProgress: (ratio: number) => void, signal?: AbortSignal): Promise<UploadedMedia> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', '/api/media?purpose=image');
    xhr.withCredentials = true;
    xhr.setRequestHeader('Accept', 'application/json');
    xhr.setRequestHeader('X-Requested-With', 'natashu');
    if (csrfToken) xhr.setRequestHeader('X-CSRF-Token', csrfToken);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      let parsed: unknown = null;
      try {
        parsed = JSON.parse(xhr.responseText);
      } catch {
        parsed = null;
      }
      if (xhr.status >= 200 && xhr.status < 300 && parsed) {
        resolve(parsed as UploadedMedia);
        return;
      }
      const envelope = parsed as Partial<ApiErrorBody> | null;
      if (envelope?.error?.code) {
        reject(
          new ApiError({
            code: envelope.error.code,
            message: envelope.error.message || ERROR_SPECS[envelope.error.code]?.th || ERROR_SPECS.INTERNAL.th,
            retryable: Boolean(envelope.error.retryable),
            status: xhr.status,
            details: envelope.error.details,
            correlationId: envelope.error.correlationId ?? null,
          }),
        );
        return;
      }
      if (xhr.status === 413) {
        reject(new ApiError({ code: 'PAYLOAD_TOO_LARGE', message: ERROR_SPECS.PAYLOAD_TOO_LARGE.th, retryable: false, status: 413 }));
        return;
      }
      reject(new ApiError({ code: 'NETWORK', message: NETWORK_ERROR_MESSAGE, retryable: true, status: xhr.status }));
    };
    xhr.onerror = () => reject(new ApiError({ code: 'NETWORK', message: NETWORK_ERROR_MESSAGE, retryable: true, status: 0 }));
    xhr.onabort = () => reject(new DOMException('aborted', 'AbortError'));
    signal?.addEventListener('abort', () => xhr.abort(), { once: true });
    const form = new FormData();
    form.append('file', file, file.name);
    xhr.send(form);
  });
}

/** Newest server message in the loaded pages (pages are newest-first). */
export function newestMessageId(data: InfiniteData<MessagesPage> | undefined): string | null {
  return data?.pages[0]?.items[0]?.id ?? null;
}
